/**
 * Background music player. Cycles through a fixed list of track URLs, with the
 * first track picked at random. When a track ends, the next in cyclic order
 * plays. Once started, music keeps running across phases; setPaused integrates
 * with the global game-pause control.
 *
 * The audio element and the initial-index picker are injected, so tests can
 * stub both and assert behavior deterministically.
 *
 * Browsers gate audio playback until the first user gesture, so the caller
 * must trigger start() from a keydown/pointerdown listener.
 */

export type MusicAudio = {
  src: string;
  volume: number;
  preload: string;
  addEventListener(event: 'ended', listener: () => void): void;
  play(): Promise<void>;
  pause(): void;
};

export type MusicPlayer = {
  /** Begin playback at the chosen starting track. Idempotent. */
  start(): Promise<void>;
  /** Pause/resume the current track without losing the queue position. */
  setPaused(paused: boolean): void;
  /** 0..1, clamped. */
  setVolume(v: number): void;
};

export type MusicPlayerOptions = {
  readonly tracks: readonly string[];
  readonly audio: MusicAudio;
  /** Picks the initial track index. Defaults to a uniform random pick. */
  readonly pickInitialIndex?: (length: number) => number;
};

export function createMusicPlayer(opts: MusicPlayerOptions): MusicPlayer {
  if (opts.tracks.length === 0) {
    return {
      async start() {},
      setPaused() {},
      setVolume() {},
    };
  }

  const tracks = opts.tracks;
  const audio = opts.audio;
  const pickInitial = opts.pickInitialIndex ?? ((n) => Math.floor(Math.random() * n));

  const raw = pickInitial(tracks.length);
  let idx = Number.isFinite(raw) ? Math.min(Math.max(0, Math.floor(raw)), tracks.length - 1) : 0;
  let started = false;
  let paused = false;
  let volume = 0.5;

  audio.preload = 'auto';

  audio.addEventListener('ended', () => {
    idx = (idx + 1) % tracks.length;
    audio.src = tracks[idx]!;
    if (!paused) void audio.play().catch(() => {});
  });

  return {
    async start() {
      if (started) return;
      audio.volume = volume;
      audio.src = tracks[idx]!;
      try {
        await audio.play();
        started = true;
      } catch {
        // Autoplay still blocked; caller can retry from a later gesture.
      }
    },
    setPaused(p) {
      paused = p;
      if (!started) return;
      if (p) audio.pause();
      else void audio.play().catch(() => {});
    },
    setVolume(v) {
      volume = v < 0 ? 0 : v > 1 ? 1 : v;
      audio.volume = volume;
    },
  };
}
